import { CreatePZDto, PZItem } from '../../types/pz';
import { isWarehouseQuantity, roundWarehouseQuantity } from '../../utils/warehouseQuantity';

type PZItemPayload = CreatePZDto['items'][number];

interface BuildPZItemsPayloadOptions {
  canEditPurchasePrices: boolean;
  documentCurrency: string;
}

export type PZItemDraft = Omit<Partial<PZItem>, 'quantityOrdered' | 'unitPrice'> & {
  // Ant Design InputNumber emits null when the operator clears the field.
  quantityOrdered?: number | null;
  unitPrice?: number | null;
};

export type PZItemValidationKey =
  | 'itemsRequired'
  | 'quantityRequired'
  | 'quantityPositive'
  | 'priceRequired'
  | 'pricePositive';

const hasProductVariant = (
  item: PZItemDraft
): item is PZItemDraft & { productVariantId: string } =>
  typeof item.productVariantId === 'string' && item.productVariantId.trim().length > 0;

const isMissingNumber = (value: unknown): boolean => (
  value === null || value === undefined || value === ''
);

export function validatePZItemsForSave(
  items: PZItemDraft[],
  options: Pick<BuildPZItemsPayloadOptions, 'canEditPurchasePrices'>,
): PZItemValidationKey | null {
  const selectedItems = items.filter(hasProductVariant);
  if (selectedItems.length === 0) return 'itemsRequired';

  for (const item of selectedItems) {
    if (isMissingNumber(item.quantityOrdered)) {
      return 'quantityRequired';
    }
    const quantity = Number(item.quantityOrdered);
    if (!isWarehouseQuantity(quantity)) return 'quantityPositive';

    if (options.canEditPurchasePrices) {
      if (isMissingNumber(item.unitPrice)) {
        return 'priceRequired';
      }
      const unitPrice = Number(item.unitPrice);
      if (!Number.isFinite(unitPrice) || unitPrice <= 0) return 'pricePositive';
    }
  }

  return null;
}

export function buildPZItemsPayload(
  items: PZItemDraft[],
  options: BuildPZItemsPayloadOptions
): PZItemPayload[] {
  return items
    .filter(hasProductVariant)
    .map((item) => ({
      id: item.id,
      productVariantId: item.productVariantId,
      // Validation owns required/default semantics. Never silently convert a
      // cleared operator field into one received piece.
      quantityOrdered: roundWarehouseQuantity(item.quantityOrdered),
      quantityReceived: item.quantityReceived,
      width: item.width ?? null,
      height: item.height ?? null,
      length: item.length ?? null,
      thickness: item.thickness ?? null,
      netWeight: item.netWeight ?? null,
      grossWeight: item.grossWeight ?? null,
      conversionUnit: item.conversionUnit ?? null,
      unitPrice: options.canEditPurchasePrices ? Number(item.unitPrice) : 0,
      exchangeRatePLN: options.canEditPurchasePrices ? item.exchangeRatePLN : undefined,
      currency: item.currency || options.documentCurrency,
      ean: item.ean,
      notes: item.notes,
    }));
}
